import { useEffect, useState } from "react";
import { api } from "@/lib/api/client";
import { ACTION } from "@/lib/auth/actions";
import { useAuthV2 as useAuth } from "@/lib/authv2/store";
import { useCustomerBookingBadge } from "@/features/cskh/api";
import { canAccessCustomerBookingByIdentity } from "@/features/cskh/bookingAccess";
import { useChatWaitingBadge } from "@/features/customer-chat/useChatWaitingBadge";
import { useUnreadCount } from "@/features/notifications/api";
import { useLeadNowSync } from "@/features/order-dispatch/api";
import { useKhoConfigSync, useKhoFlag } from "@/features/kho/api";
import { useSealedInboxMeSync } from "@/features/sealed-inbox/api";
import { useSealedInboxFlag } from "@/features/sealed-inbox/flag";
import { useStaffChannelFlagSync } from "@/features/staff-channel/api";
import { useStaffChannelFlag } from "@/features/staff-channel/flag";
import { useShiftLeadStore } from "@/lib/auth/shift-lead";
import { NAV_GROUPS, ROLES, type NavItem } from "./sidebar/nav-items";
import { qcLabel } from "@/lib/auth/role-labels";
import { ROLE } from "@/lib/auth/roles";
import { groupQualityForQc } from "./sidebar/qc-quality-group";
import { groupDailyForReception } from "./sidebar/reception-daily-group";
import { groupDailyForCskh } from "./sidebar/cskh-daily-group";
import { isCskhDesk, isReceptionDesk } from "@/lib/auth/page-access";
import { resolveNavItem } from "./sidebar/resolve-nav-item";

export function useSidebarData() {
  const staff = useAuth((s) => s.currentStaff);
  const displayName = useAuth((s) => s.displayName);
  const casdoorSub = useAuth((s) => s.casdoorSub);
  // Subscribe to permissions so the content-gate filter re-runs when the
  // claim Set hydrates (some gates read it via hasPermission).
  const permissions = useAuth((s) => s.permissions);
  // Ca trưởng theo ca (CEO 27/09/2026): nạp "đang là ca trưởng ca nào" và đăng
  // ký nghe kho, để mục "Bảng suất cơ sở" hiện/ẩn theo giờ ca mà không cần
  // tải lại trang. Chỉ người đã liên kết hồ sơ mới làm ca trưởng được.
  useLeadNowSync(!!staff);
  // Công tắc mục Kho cho cổng menu (đọc đồng bộ qua useKhoFlag).
  useKhoConfigSync(!!staff);
  const khoOn = useKhoFlag((s) => s.enabled);
  // Hộp thư kín: bật/tắt + có tên trong danh sách đọc + số chưa xem, cho cổng
  // menu và viên đỏ (QĐ 0272).
  useSealedInboxMeSync(!!staff);
  const sealedOn = useSealedInboxFlag((s) => s.enabled);
  const sealedReader = useSealedInboxFlag((s) => s.isReader);
  // Kênh nhân sự: công tắc luồng kenh_nhan_su cho cổng menu.
  useStaffChannelFlagSync(!!staff);
  const staffChannelOn = useStaffChannelFlag((s) => s.enabled);
  const shiftLeads = useShiftLeadStore((s) => s.leads);

  const identity = staff
    ? { id: staff.id, name: staff.name, role: staff.role }
    : displayName
      ? { id: casdoorSub ?? "", name: displayName, role: "" }
      : null;

  const baseRole = ROLES[identity?.role ?? ""] ?? { label: identity?.role || "—", color: "#8B6914" };
  // QC gọi đúng loại theo phòng (QĐ 0503); màu giữ theo vai.
  const role =
    identity?.role === ROLE.QC ? { ...baseRole, label: qcLabel(staff?.department) } : baseRole;
  const canReadCustomerBooking =
    canAccessCustomerBookingByIdentity(staff) || permissions.has(ACTION.CustomerBookingRead);
  const customerBookingBadgeQ = useCustomerBookingBadge(canReadCustomerBooking);
  // Khách đang chờ trả lời ở Chat khách (QĐ 0271) — hook tự quyết ai được hỏi.
  const chatWaitingCount = useChatWaitingBadge(staff);
  // Thông báo chưa đọc trên mục "Thông báo của tôi" — CÙNG cửa, cùng khoá với
  // chấm đỏ trên chuông, nên hai con số không bao giờ lệch nhau. Hook tự tắt
  // với vai không có `notification.read` (mục cũng ẩn với họ).
  const notificationUnreadCount = useUnreadCount().data?.total ?? 0;

  // Pending salary-proposals badge — admin/director only (bk parity).
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    if (staff?.role !== "admin" && staff?.role !== "director") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPendingCount(0);
      return;
    }
    let cancelled = false;
    api
      .GET("/salary-proposals", { params: { query: { status: "pending" } } })
      .then(({ data }) => {
        if (!cancelled) setPendingCount(Array.isArray(data) ? data.length : 0);
      })
      .catch((err: unknown) => {
        console.warn("[Sidebar] proposals load failed:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [staff?.role]);

  // Filter nav by RBAC matrix (bk getVisibleNavItems). When staff is null
  // (Casdoor session active but staff row not yet linked — Phase 6 backfill
  // pending) show full nav; BE still gates each request server-side.
  void permissions; // referenced so the hook re-renders on claim changes
  void shiftLeads;
  void sealedOn; // cổng hai mục hộp thư kín đọc kho cờ
  void sealedReader;
  void staffChannelOn; // cổng mục Kênh nhân sự đọc kho công tắc
  void khoOn; // cổng mục Kho đọc kho công tắc // cùng lý do: cổng Bảng suất đọc kho ca trưởng
  // `groupQualityForQc` chạy SAU bộ lọc quyền và chỉ SẮP XẾP LẠI: vai `qc`
  // nhận thêm nhóm "Chất lượng" ở trên cùng, mọi vai khác nhận lại đúng mảng
  // vừa lọc (xem qc-quality-group.ts).
  const roleGroups = staff
    ? groupQualityForQc(
        NAV_GROUPS
        .map((g) => ({
          ...g,
          // Show an item only when BOTH the page-access matrix allows it AND
          // its view's content gate would render (else the menu links to a
          // "Bạn không có quyền xem" page). See nav-content-gates.ts. Entries
          // folding several pages into one (suggestions inside /products)
          // resolve to the first page the user can reach — resolve-nav-item.ts.
          items: g.items
            .map((i) => resolveNavItem(i, staff))
            .filter((i): i is NavItem => i !== null),
        }))
          .filter((g) => g.items.length > 0),
        staff.role,
        staff.department,
      )
    : NAV_GROUPS;
  // `groupDailyForReception` cùng nếp: nhân sự quầy lễ tân nhận nhóm "Việc hằng
  // ngày" lên đầu (reception-daily-group.ts), vai khác nhận lại y mảng cũ.
  // Nhân sự phòng CSKH có nhóm "Việc hằng ngày" riêng (cskh-daily-group.ts);
  // một người chỉ thuộc một trong hai nhóm nên hai hàm không chồng nhau.
  const visibleGroups = groupDailyForCskh(
    groupDailyForReception(roleGroups, isReceptionDesk(staff)),
    isCskhDesk(staff),
  );

  return {
    identity,
    role,
    visibleGroups,
    pendingCount,
    customerBookingPendingCount: customerBookingBadgeQ.data ?? 0,
    chatWaitingCount,
    notificationUnreadCount,
  };
}
